import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { t, Trans } from '@lingui/macro';
import { Close as CloseIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SCHEDULE_ERROR_KEYS } from './ServiceTestsScheduleConstants';
import { generateIntervals, generateTimes, getUserTzAbbr } from './ServiceTestsScheduleGenerators';
import { prepareScheduleForSave, prepareScheduleForScreen, validateSchedule } from './ServiceTestsScheduleTransforms';
import ScheduleFields from './detail/sections/ScheduleFields';
import TestSubFormSection from './detail/sections/TestFormSection/TestSubFormSection';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
    },
}));

// Translated error messages — kept here so schedule utility modules stay React-free
const ERROR_MESSAGES = {
    [SCHEDULE_ERROR_KEYS.DAILY_START_REQUIRED]: t`Daily start time is required`,
    [SCHEDULE_ERROR_KEYS.INTERVAL_REQUIRED]: t`Interval is required`,
    [SCHEDULE_ERROR_KEYS.WINDOW_START_REQUIRED]: t`Window start time is required`,
    [SCHEDULE_ERROR_KEYS.WINDOW_END_REQUIRED]: t`Window end time is required`,
    [SCHEDULE_ERROR_KEYS.WINDOW_END_SAME_AS_START]: t`End time must differ from start time`,
};

export default function ServiceTestsScheduleDialog({
    open,
    setOpen,
    scheduling,
    typeScheduling,
    updateHandlerCallbackRef,
}) {
    const classes = useStyles();

    const intervals = useMemo(() => generateIntervals(typeScheduling?.interval), [typeScheduling]);

    // Intentionally empty — session-level preferences, stable for component lifetime (matching portal/ui behavior)
    const times = useMemo(() => generateTimes(), []);
    const tzAbbr = useMemo(() => getUserTzAbbr(), []);

    const methods = useForm({
        defaultValues: () => prepareScheduleForScreen(scheduling, intervals),
    });
    const { reset, handleSubmit, setError } = methods;

    // Reset form whenever dialog opens — handles pre-fill for single vs multi-record selection
    useEffect(() => {
        if (open) {
            reset(prepareScheduleForScreen(scheduling, intervals));
        }
    }, [open, scheduling, intervals, reset]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const onSubmit = useCallback(
        (values) => {
            const validationErrors = validateSchedule(values);
            if (Object.keys(validationErrors).length > 0) {
                Object.entries(validationErrors).forEach(([field, key]) => {
                    setError(field, { message: ERROR_MESSAGES[key] });
                });
                return;
            }
            updateHandlerCallbackRef.current(prepareScheduleForSave(values));
            handleClose();
        },
        [updateHandlerCallbackRef, handleClose, setError]
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Typography variant="h6">
                    <Trans>Select a Run Schedule</Trans>
                </Typography>
                <IconButton size="small" onClick={handleClose} aria-label={t`Close`}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <FormProvider {...methods}>
                <DialogContent dividers>
                    <TestSubFormSection>
                        <ScheduleFields intervals={intervals} times={times} tzAbbr={tzAbbr} />
                    </TestSubFormSection>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
                        <Trans>Update</Trans>
                    </Button>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
}

ServiceTestsScheduleDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    scheduling: PropTypes.shape({
        type: PropTypes.string,
        timezone: PropTypes.string,
        interval: PropTypes.number,
        start: PropTypes.string,
        end: PropTypes.string,
    }),
    typeScheduling: PropTypes.shape({
        interval: PropTypes.shape({
            min: PropTypes.number,
            max: PropTypes.number,
        }),
    }),
    updateHandlerCallbackRef: PropTypes.shape({
        current: PropTypes.func,
    }).isRequired,
};

ServiceTestsScheduleDialog.defaultProps = {
    scheduling: {},
    typeScheduling: undefined,
};
